import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { BackgroundOverlay } from '@/components/Tile';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import { IAgent } from '@d-id/types';
import { Button } from '@de-id/ui-components';
import styled from '@emotion/styled';
import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

const Title = styled.span`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
`;

const Description = styled.span`
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 14px;
    font-weight: 500;
    letter-spacing: -0.24px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const ActionsContainer = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 24px;

    button {
        display: none;
    }
`;

const StyledButton = styled(Button)`
    z-index: 1000;
    width: min-content;
    white-space: nowrap;
    opacity: 0;
    transition: all 0.2s ease-in-out;
`;

const Container = styled.div<{ active?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 4px;
    cursor: pointer;

    background-color: ${theme.colors.white};
    border-radius: 10px;
    border: 1px solid ${({ theme, active }) => (active ? theme.colors.primary.main : theme.colors.black[300])};

    :hover {
        ${ActionsContainer} {
            button {
                display: block;
            }
        }

        .BackgroundOverlay {
            opacity: 0.5;
            transform: scale(105%);
        }

        ${StyledButton} {
            opacity: 1;
        }
    }
`;

const ImageContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    aspect-ratio: 16/9;
    justify-content: center;
    align-items: center;

    position: relative;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
`;

const InfoContainer = styled.div`
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

interface Props {
    template: IAgent;
    clientKey: any;
    active?: boolean;
}

const agentsDescriptions: Record<string, string> = {
    v2_agt_ZKJEaHuV: 'Description for the agent v2_agt_ZKJEaHuV which is dev template',
    v2_agt_gL2BIsAW: "D-ID's witty and playful face, bringing the tech to life with charm",
    v2_agt_6s8X40sT: 'Laid-back, well-traveled guide who helps you plan trips with insider tips and cultural gems',
    v2_agt_umRtTPFu: 'Master of role-play, ready for any scene. Making demos smooth, fun, and unforgettable',
    v2_agt_ju0h06HW: 'Tech-savvy SDR who makes sales chats effortless',
    v2_agt_4i425Kpe: 'Tech-savvy SDR who makes sales chats effortless',
    v2_agt_5DA2_vmn: 'A witty brand ambassador that brings your brand to life',
};

const AgentTemplateTile = ({ template, clientKey, active }: Props) => {
    const navigate = useNavigate();
    const mixpanel = useAnalytics();

    const handleTemplatePreview = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();

        if (clientKey) {
            mixpanel?.track('agent-template', { event: 'click', from: 'agent-gallery', template: template.id });

            navigate(`${AppRoutes.AgentTemplate}/${template.id}`, { state: { from: 'agent-gallery' } });
        }
    };

    return (
        <Container onClick={handleTemplatePreview} active={active}>
            <ImageContainer>
                <StyledButton color="secondary" size="small">
                    Preview template
                </StyledButton>
                <BackgroundOverlay bg={template.preview_thumbnail || ''} bgColor="rgba(255, 255, 255, 0.5)" showBlur={false} />
            </ImageContainer>
            <InfoContainer>
                <Title>{template.preview_name}</Title>
                <Description>{agentsDescriptions[template.id] || ''}</Description>
            </InfoContainer>
        </Container>
    );
};

const AgentTemplateTileErrorBoundary = (props: Props): ReactElement => (
    <ErrorBoundary
        fallback={<AgentTemplateTile {...props} />}
        beforeCapture={scope => scope.setTags({ component: 'AgentTemplateTile', id: props.template.id, status: props.template.status })}>
        <AgentTemplateTile {...props} />
    </ErrorBoundary>
);

export { AgentTemplateTileErrorBoundary as AgentTemplateTile };
